import React, { useState, useEffect, useRef, useCallback } from "react";
import correctSound from "../../../Assets/final new sound/correct-2-46134.mp3";
import wrongSound from "../../../Assets/final new sound/invalid-selection-39351.mp3";
import { useNavigate } from "react-router";
import AuthUser from "../../../Hooks/authUser";
import moment from "moment";
import { server_url } from "../../../Config/API";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import { getUserHook } from "../../../Hooks/getUserHook";
import GameStatsIndicator from "../GameStatsIndicator";

const RedBlueSearchQuest = () => {
  const [tableData, setTableData] = useState([]);
  const [suggestion, setSuggestion] = useState("");
  
  const correctAudioRef = useRef(null);
  const wrongAudioRef = useRef(null);
  if (typeof window !== "undefined") {
    if (!correctAudioRef.current) correctAudioRef.current = new Audio(correctSound);
    if (!wrongAudioRef.current) wrongAudioRef.current = new Audio(wrongSound);
  }

  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [suggestionClicks, setSuggestionClicks] = useState(0);

  const [timeLeft, setTimeLeft] = useState(300);
  const [score, setScore] = useState(0);
  const [searchQuestData, setSearchQuestData] = useState([]);

  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;

  const previousStateRef = useRef(null);
  const scoreRef = useRef(score);
  const timeRef = useRef(timeLeft);
  const suggestionRef = useRef(suggestion);
  const tableDataRef = useRef(tableData);
  const suggestionClicksRef = useRef(suggestionClicks);
  const usedCharactersRef = useRef(new Set());

  const blueTextColor = "blue";
  const redTextColor = "red";
  const [textColorClass, setTextColorClass] = useState(blueTextColor);
  const [suggestionColorClass, setSuggestionColorClass] = useState(blueTextColor);

  scoreRef.current = score;
  timeRef.current = timeLeft;
  suggestionRef.current = suggestion;
  tableDataRef.current = tableData;
  suggestionClicksRef.current = suggestionClicks;

  // Red blue settings
  const [user, setUser] = useState([]);
  const [blue, setBlue] = useState(100);
  const [red, setRed] = useState(100);

  useEffect(() => {
    getUserHook(userInfo?._id, setUser);
  }, []);

  useEffect(() => {
    if (user) {
      setRed(user?.redOpacity ?? 100);
      setBlue(user?.blueOpacity ?? 100);
    }
  }, [user]);

  const generateRandomData = () => {
    const characters = "!?:;-.+-×÷=><€£¥₹$₿0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const availableCharacters = characters.split("").filter(char => !usedCharactersRef.current.has(char));

    if (availableCharacters.length === 0) {
      usedCharactersRef.current.clear();
      return characters[Math.floor(Math.random() * characters.length)];
    }

    const randomIndex = Math.floor(Math.random() * availableCharacters.length);
    const randomCharacter = availableCharacters[randomIndex];
    usedCharactersRef.current.add(randomCharacter);
    return randomCharacter;
  };

  const generateSuggestion = (currentTableData) => {
    const dataList = currentTableData || tableDataRef.current;
    if (dataList.length === 0) return;
    const randomIndex = Math.floor(Math.random() * dataList.length);
    setSuggestion(dataList[randomIndex]);
    setSuggestionColorClass(textColorClass);
  };

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/searchQuest/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}&fieldName3=gameName&fieldValue3=Search+Quest+Binocular`
      );
      const data = await response.json();
      setSearchQuestData(data?.data || []);
      if (data?.data && data?.data.length) {
        setScore(data?.data[0]?.score);
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching red-blue search quest data:", error);
    }
  };

  useEffect(() => {
    if (searchQuestData?.length) {
      setTimeLeft(searchQuestData[0]?.remainingTime);
      setScore(searchQuestData[0]?.score);
    }
  }, [searchQuestData]);

  useEffect(() => {
    fetchFunction();
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/searchQuest/updateSearchQuest/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/searchQuest/addSearchQuest`,
        {
          gameName: "Search Quest Binocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        },
        "searchQuest score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const insertRandomData = (currentTable) => {
    const newData = generateRandomData();
    setTableData([...currentTable, newData]);
  };

  const resetGame = () => {
    usedCharactersRef.current.clear();
    const initialTableData = [];
    for (let i = 0; i < 48; i++) {
      initialTableData.push(generateRandomData());
    }
    setTableData(initialTableData);
    generateSuggestion(initialTableData);
    setSuggestionClicks(0);
  };

  const handleCellClick = (rowData) => {
    if (rowData === suggestionRef.current) {
      setScore((prevScore) => prevScore + 1);
      setSuggestionClicks((prevClicks) => prevClicks + 1);

      if (correctAudioRef.current) {
        correctAudioRef.current.currentTime = 0;
        correctAudioRef.current.play().catch(e => console.log(e));
      }

      setTextColorClass((prevClass) =>
        prevClass === blueTextColor ? redTextColor : blueTextColor
      );

      if (suggestionClicksRef.current >= 2) {
        resetGame();
      } else {
        const updatedTableData = [...tableDataRef.current];
        updatedTableData.splice(updatedTableData.indexOf(rowData), 1);
        insertRandomData(updatedTableData);
      }
    } else {
      if (wrongAudioRef.current) {
        wrongAudioRef.current.currentTime = 0;
        wrongAudioRef.current.play().catch(e => console.log(e));
      }
    }
  };

  const handleGameOver = () => {
    navigate(-1);
    if (toggleFullScreenRef.current) toggleFullScreenRef.current();
  };

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          insertFunctionRef.current();
          handleGameOver();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, []);

  useEffect(() => {
    const initialTableData = [];
    for (let i = 0; i < 48; i++) {
      initialTableData.push(generateRandomData());
    }
    setTableData(initialTableData);
    generateSuggestion(initialTableData);
  }, []);

  const toggleFullScreenRef = useRef(null);
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.error("Error entering full screen:", error);
        });
      }
    }
    setIsFullScreen(!isFullScreen);
  };
  toggleFullScreenRef.current = toggleFullScreen;

  useEffect(() => {
    toggleFullScreen();
  }, []);

  useEffect(() => {
    if (tableData.length > 0) {
      generateSuggestion(tableData);
    }
  }, [tableData]);

  useEffect(() => {
    const handleKeyPress = async (event) => {
      if (event.keyCode === 27 && isFullScreen) {
        insertFunctionRef.current();
        await navigate(-1);
        if (toggleFullScreenRef.current) toggleFullScreenRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFullScreen, navigate]);

  return (
    <div
      className={`flex items-center justify-center inset-0 fixed bg-black ${
        isFullScreen ? "h-screen" : "min-h-screen"
      } bg-black`}
    >
      <GameStatsIndicator score={score} timeLeft={timeLeft} />
      <div
        className={`w-full ${
          isFullScreen ? "max-h-screen" : "max-w-screen-lg"
        } bg-black rounded-lg  p-6`}
      >
        <div className="grid grid-cols-12 text-[70px]  gap-5 justify-around text-white">
          {tableData.map((rowData, index) => (
            <div
              key={index}
              style={{
                color: suggestionColorClass,
                opacity: (suggestionColorClass === "red" ? red : blue) / 100,
              }}
              className="px-4 py-2   cursor-pointer"
              onClick={() => handleCellClick(rowData)}
            >
              {rowData}
            </div>
          ))}
        </div>

        <div
          style={{ color: suggestionColorClass }}
          className="border-t-4 w-full flex justify-between items-center py-4"
        >
          <h1 className="text-[70px] font-sans italic leading-none">
            Search: <span className="text-[70px]" style={{
              opacity: (suggestionColorClass === "red" ? red : blue) / 100
            }}>{suggestion}</span>
          </h1>
        </div>
      </div>
    </div>
  );
};

export default RedBlueSearchQuest;
